/**
 * HmPaymentNew — wystawienie dokumentu kasowego (kasa i bank).
 *
 * DWIE RZECZY ODRÓŻNIAJĄ TEN EKRAN OD FAKTURY:
 *
 *  1. RODZAJ DOKUMENTU STEROWUJE CAŁYM FORMULARZEM. Wpłata i wypłata (KP/KW/BP/BW) MOGĄ wskazać
 *     kontrahenta i rozliczyć jego faktury — ale nie muszą (decyzja właściciela 2026-09-14:
 *     zasilenie kasy czy wpłata własna nie mają podmiotu, a wymuszanie go kazałoby wpisywać
 *     nieprawdę). „Inne przyjęcie/wydanie" (IP/IW) to operacja z definicji bezpodmiotowa;
 *     transfer (TR−) przesuwa środki między rejestrami i zamiast kontrahenta ma rejestr docelowy.
 *     Pokazujemy tylko pola, które dla danego rodzaju mają sens.
 *
 *  2. NIE MA BUFORA ANI EDYCJI. WebAPI Symfonii nie pozwala poprawić ani usunąć wystawionego
 *     dokumentu kasowego (jedyny zapis poza tworzeniem to przyjęcie transferu). Dlatego jest
 *     jeden przycisk — z potwierdzeniem — i nie ma odpowiednika „zapisz do bufora".
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  HmContractorListItem,
  HmListResponse,
  HmPaymentDictionaries,
  HmPaymentIssueInput,
  HmPaymentIssueResult,
  HmPaymentKind,
  HmSettlementListItem,
} from '@saas/shared';
import { HM_PAYMENT_KINDS, hmPaymentIsTransfer, hmPaymentSupportsSubject } from '@saas/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Send } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DatePicker } from '@/components/DatePicker';
import { Input } from '@/components/Input';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

/** Zaznaczony rozrachunek wraz z kwotą do rozliczenia (tekst — idzie z pola formularza). */
interface SelectedSettlement {
  item: HmSettlementListItem;
  value: string;
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function num(value: string): number {
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function money(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2);
}

export function HmPaymentNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const companyId = useActiveCompanyScope().companyId;

  const [kind, setKind] = useState<HmPaymentKind>('KP');
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [series, setSeries] = useState('');
  const [registryId, setRegistryId] = useState('');
  const [targetRegistryId, setTargetRegistryId] = useState('');
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [totalValue, setTotalValue] = useState('');
  const [paymentDate, setPaymentDate] = useState(today());
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState<Record<number, SelectedSettlement>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  const q = `companyId=${companyId}`;
  const transfer = hmPaymentIsTransfer(kind);
  // Kontrahent jest OPCJONALNY — pokazujemy pole tam, gdzie ma sens, ale nie blokujemy
  // wystawienia bez niego (decyzja właściciela 2026-09-14).
  const supportsSubject = hmPaymentSupportsSubject(kind);

  const dictionaries = useQuery({
    queryKey: ['handel-magazyn', 'payment-dictionaries', companyId, kind],
    enabled: !!companyId,
    retry: false,
    queryFn: async () =>
      (
        await api.get<HmPaymentDictionaries>(
          `/handel-magazyn/payment-dictionaries?kind=${kind}&${q}`,
        )
      ).data,
  });

  const contractors = useQuery({
    queryKey: ['handel-magazyn', 'contractors', companyId],
    enabled: !!companyId && supportsSubject,
    retry: false,
    queryFn: async () =>
      (await api.get<HmListResponse<HmContractorListItem>>(`/handel-magazyn/contractors?${q}`)).data
        .items,
  });

  const seriesQuery = useQuery({
    queryKey: ['handel-magazyn', 'payment-series', companyId, documentTypeId],
    enabled: !!companyId && !!documentTypeId,
    retry: false,
    queryFn: async () =>
      (
        await api.get<{ items: { id: number; code: string }[] }>(
          `/handel-magazyn/payment-series?documentTypeId=${documentTypeId}&${q}`,
        )
      ).data.items,
  });

  /** Rozrachunki pobieramy dopiero, gdy rodzaj dokumentu w ogóle je rozlicza. */
  const settlements = useQuery({
    queryKey: ['handel-magazyn', 'settlements', companyId, subjectId],
    enabled: !!companyId && !transfer && !!subjectId,
    retry: false,
    queryFn: async () =>
      (
        await api.get<HmListResponse<HmSettlementListItem>>(
          `/handel-magazyn/settlements?contractorId=${subjectId}&${q}`,
        )
      ).data.items,
  });

  const dict = dictionaries.data;

  // Zmiana rodzaju unieważnia typ, serię i rozliczenia — typy są per rodzaj, a rozliczeń
  // transfer w ogóle nie ma. Zostawienie ich dałoby dokument, który Symfonia odrzuci.
  useEffect(() => {
    setDocumentTypeId('');
    setSeries('');
    setSelected({});
    if (hmPaymentIsTransfer(kind)) setSubjectId(null);
    else setTargetRegistryId('');
  }, [kind]);

  useEffect(() => {
    setSeries('');
  }, [documentTypeId]);

  useEffect(() => {
    const items = seriesQuery.data;
    if (items?.length) setSeries((prev) => prev || (items[0]?.code ?? ''));
  }, [seriesQuery.data]);

  // Zmiana kontrahenta zmienia listę jego rozrachunków — stare zaznaczenia przestają obowiązywać.
  useEffect(() => {
    setSelected({});
  }, [subjectId]);

  const selectedList = useMemo(() => Object.values(selected), [selected]);
  const settlementsSum = useMemo(
    () => selectedList.reduce((acc, s) => acc + num(s.value), 0),
    [selectedList],
  );

  function toggleSettlement(item: HmSettlementListItem, checked: boolean) {
    setSelected((prev) => {
      const next = { ...prev };
      // Domyślna kwota to CAŁA pozostała kwota rozrachunku — najczęstszy przypadek to zapłata
      // w całości; kwotę częściową użytkownik poprawia ręcznie.
      if (checked) next[item.id] = { item, value: item.leftToPayValue };
      else delete next[item.id];
      return next;
    });
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: HmPaymentIssueInput = {
        kind,
        documentTypeCode:
          dict?.documentTypes.find((d) => String(d.id) === documentTypeId)?.code ?? '',
        series,
        paymentRegistryId: Number(registryId),
        ...(supportsSubject && subjectId ? { subjectId: Number(subjectId) } : {}),
        ...(transfer && targetRegistryId
          ? { targetPaymentRegistryId: Number(targetRegistryId) }
          : {}),
        ...(totalValue.trim() ? { totalValue: num(totalValue) } : {}),
        ...(paymentDate ? { paymentDate } : {}),
        ...(note ? { note } : {}),
        ...(selectedList.length
          ? {
              settlements: selectedList.map((s) => ({
                settlementId: s.item.id,
                documentId: s.item.documentId,
                documentNumber: s.item.documentNumber,
                value: num(s.value),
              })),
            }
          : {}),
      };
      return (await api.post<HmPaymentIssueResult>(`/handel-magazyn/payments?${q}`, payload)).data;
    },
    onSuccess: (doc) => {
      toast.show({
        variant: 'success',
        title: t('tenant.handelMagazyn.newPayment.toast.issued', { number: doc.documentNumber }),
      });
      navigate(`/handel-magazyn/kasa-bank/${doc.id}`);
    },
    onError: (err) => {
      const data = isAxiosError(err)
        ? (err.response?.data as
            | {
                message?: string;
                details?: { body?: string };
                fieldErrors?: { field: string; message: string }[];
              }
            | undefined)
        : undefined;
      // Nasze sprawdzenia wracają listą pól — pokazujemy je wprost, bo to one mówią, co poprawić.
      const fieldErrors = (data?.fieldErrors ?? []).map((e) => e.message).join(' · ');
      toast.show({
        variant: 'error',
        title: t('tenant.handelMagazyn.newPayment.toast.failed'),
        description: fieldErrors || data?.details?.body || data?.message || undefined,
      });
    },
  });

  const canSubmit =
    !!companyId &&
    !!documentTypeId &&
    !!series &&
    !!registryId &&
    (!transfer || (!!targetRegistryId && num(totalValue) > 0)) &&
    // Kontrahent nie jest wymagany, ale kwota TAK — gdy nie ma rozliczeń, Symfonia nie ma
    // z czego jej wyliczyć, a dokumentu kasowego nie da się później poprawić ani usunąć.
    (transfer || selectedList.length > 0 || num(totalValue) > 0) &&
    !mutation.isPending;

  if (!companyId) {
    return (
      <div className="p-4 text-sm text-text-secondary">{t('tenant.handelMagazyn.pickCompany')}</div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">
            {t('tenant.handelMagazyn.newPayment.title')}
          </h1>
          <p className="text-sm text-text-secondary">
            {t('tenant.handelMagazyn.newPayment.subtitle')}
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<Send className="h-4 w-4" />}
          disabled={!canSubmit}
          onClick={() => setConfirmOpen(true)}
        >
          {t('tenant.handelMagazyn.newPayment.issue')}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 rounded-md border border-border-primary bg-bg-secondary p-4 lg:grid-cols-2">
        <div className="space-y-3">
          <Select
            label={t('tenant.handelMagazyn.newPayment.kind')}
            value={kind}
            onChange={(e) => setKind(e.target.value as HmPaymentKind)}
            options={HM_PAYMENT_KINDS.map((k) => ({
              value: k,
              label: t(`tenant.handelMagazyn.newPayment.kinds.${k}`),
            }))}
          />
          <Select
            label={t('tenant.handelMagazyn.newSale.documentType')}
            value={documentTypeId}
            onChange={(e) => setDocumentTypeId(e.target.value)}
            placeholder={t('tenant.handelMagazyn.newSale.choose')}
            options={(dict?.documentTypes ?? []).map((d) => ({
              value: String(d.id),
              label: `${d.code} — ${d.name}`,
            }))}
          />
          <Select
            label={t('tenant.handelMagazyn.newSale.series')}
            value={series}
            onChange={(e) => setSeries(e.target.value)}
            disabled={!documentTypeId || seriesQuery.isLoading}
            placeholder={t('tenant.handelMagazyn.newSale.chooseAfterType')}
            options={(seriesQuery.data ?? []).map((s) => ({ value: s.code, label: s.code }))}
          />
        </div>

        <div className="space-y-3">
          <Select
            label={
              transfer
                ? t('tenant.handelMagazyn.newPayment.sourceRegistry')
                : t('tenant.handelMagazyn.newSale.paymentRegistry')
            }
            value={registryId}
            onChange={(e) => setRegistryId(e.target.value)}
            placeholder={t('tenant.handelMagazyn.newSale.choose')}
            options={(dict?.paymentRegistries ?? []).map((r) => ({
              value: String(r.id),
              label: r.name,
            }))}
          />

          {/* Transfer ma rejestr docelowy ZAMIAST kontrahenta — nigdy oba naraz. */}
          {transfer ? (
            <Select
              label={t('tenant.handelMagazyn.newPayment.targetRegistry')}
              value={targetRegistryId}
              onChange={(e) => setTargetRegistryId(e.target.value)}
              placeholder={t('tenant.handelMagazyn.newSale.choose')}
              hint={t('tenant.handelMagazyn.newPayment.targetHint')}
              options={(dict?.paymentRegistries ?? [])
                .filter((r) => String(r.id) !== registryId)
                .map((r) => ({ value: String(r.id), label: r.name }))}
            />
          ) : supportsSubject ? (
            <div>
              <span className="mb-1 block text-sm font-medium text-text-primary">
                {t('tenant.handelMagazyn.newPayment.subject')}
              </span>
              <SearchableSelect
                value={subjectId}
                onChange={setSubjectId}
                allowClear
                options={(contractors.data ?? []).map((c) => ({
                  value: String(c.id),
                  label: c.name,
                  description: [c.code, c.taxId ? `NIP ${c.taxId}` : null]
                    .filter(Boolean)
                    .join(' · '),
                }))}
                placeholder={t('tenant.handelMagazyn.newSale.buyerPlaceholder')}
                data-testid="hm-payment-subject"
              />
            </div>
          ) : (
            <p className="rounded-md border border-border-primary bg-bg-tertiary px-3 py-2 text-xs text-text-secondary">
              {t('tenant.handelMagazyn.newPayment.noSubjectHint')}
            </p>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Input
              label={t('tenant.handelMagazyn.newPayment.amount')}
              value={totalValue}
              inputMode="decimal"
              onChange={(e) => setTotalValue(e.target.value)}
              hint={
                transfer
                  ? t('tenant.handelMagazyn.newPayment.amountTransferHint')
                  : selectedList.length > 0
                    ? t('tenant.handelMagazyn.newPayment.amountHint')
                    : t('tenant.handelMagazyn.newPayment.amountRequiredHint')
              }
              data-testid="hm-payment-amount"
            />
            <label className="flex flex-col text-sm text-text-secondary">
              {t('tenant.handelMagazyn.newPayment.date')}
              <DatePicker value={paymentDate} onChange={(v) => setPaymentDate(v || '')} />
            </label>
          </div>
        </div>
      </div>

      {/* Rozliczenia tylko tam, gdzie mają sens: transfer nie zamyka rozrachunków. */}
      {!transfer && (
        <div className="mt-4 rounded-md border border-border-primary bg-bg-secondary p-3">
          <h2 className="mb-2 text-sm font-semibold text-text-primary">
            {t('tenant.handelMagazyn.newPayment.settlements')}
          </h2>

          {!subjectId ? (
            <p className="py-4 text-center text-sm text-text-tertiary">
              {t('tenant.handelMagazyn.newPayment.pickSubjectFirst')}
            </p>
          ) : settlements.isLoading ? (
            <p className="py-4 text-center text-sm text-text-secondary">{t('common.loading')}</p>
          ) : (settlements.data ?? []).length === 0 ? (
            <p className="py-4 text-center text-sm text-text-tertiary">
              {t('tenant.handelMagazyn.newPayment.noSettlements')}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-primary text-left text-xs uppercase tracking-wide text-text-tertiary">
                  <th className="w-10 px-2 py-1.5" />
                  <th className="px-2 py-1.5 font-medium">
                    {t('tenant.handelMagazyn.columns.documentNumber')}
                  </th>
                  <th className="px-2 py-1.5 font-medium">
                    {t('tenant.handelMagazyn.columns.maturityDate')}
                  </th>
                  <th className="px-2 py-1.5 text-right font-medium">
                    {t('tenant.handelMagazyn.newPayment.leftToPay')}
                  </th>
                  <th className="px-2 py-1.5 text-right font-medium">
                    {t('tenant.handelMagazyn.newPayment.settleAmount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(settlements.data ?? []).map((item) => {
                  const picked = selected[item.id];
                  return (
                    <tr key={item.id} className="border-b border-border-primary/60">
                      <td className="px-2 py-1.5">
                        <Checkbox
                          checked={!!picked}
                          onChange={(e) => toggleSettlement(item, e.target.checked)}
                          aria-label={item.documentNumber}
                          data-testid={`hm-settlement-${item.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 font-mono text-xs text-text-primary">
                        {item.documentNumber}
                      </td>
                      <td className="px-2 py-1.5 text-text-secondary">
                        {item.maturityDate?.slice(0, 10) ?? '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right text-text-secondary">
                        {item.leftToPayValue}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {picked ? (
                          <Input
                            value={picked.value}
                            inputMode="decimal"
                            className="text-right"
                            onChange={(e) =>
                              setSelected((prev) => ({
                                ...prev,
                                [item.id]: { item, value: e.target.value },
                              }))
                            }
                            data-testid={`hm-settlement-value-${item.id}`}
                          />
                        ) : (
                          <span className="text-text-tertiary">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {selectedList.length > 0 && (
            <p className="mt-2 text-right text-sm text-text-secondary">
              {t('tenant.handelMagazyn.newPayment.settlementsSum', {
                value: money(settlementsSum),
              })}
            </p>
          )}
        </div>
      )}

      <div className="mt-3">
        <Textarea
          label={t('tenant.handelMagazyn.detail.note')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
        />
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          mutation.mutate();
        }}
        variant="primary"
        confirming={mutation.isPending}
        title={t('tenant.handelMagazyn.newPayment.confirmTitle')}
        message={t('tenant.handelMagazyn.newPayment.confirmBody')}
        confirmLabel={t('tenant.handelMagazyn.newPayment.issue')}
      />
    </div>
  );
}

export default HmPaymentNew;
